/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import colors from '@sportninja/common/constants/appColors'
import React, { useState, useEffect, useCallback, useRef } from 'react'
import { Spin, FloatButton } from 'antd'
import { ArrowUpOutlined, CalendarOutlined } from '@ant-design/icons'
import { connect } from 'react-redux'
import {
  getGameStatusTypesBySportId,
  getAllGameTypes,
} from '@sportninja/common/selectors/types'
import { useMyScheduleContext } from 'src/context/MyScheduleContext'
import FilterBar from '../components/FilterBar'
import Text from 'src/components/ts-components/Text'
import { DayOfGames } from 'src/components/List/GameList'
import { Mobile } from 'src/components/Responsive'
import { formatCivilDay } from 'src/components/List/GameList/CalendarView/buildCalendarDays'
import EventListRow from 'src/features/event/components/EventListRow'
import { groupGamesByDay } from '@sportninja/common/features/game/gameListView/gameDayBucket'
import { clampEventsToLoadedGames } from '@sportninja/common/features/event/clampEventsToLoadedGames'
import { filterEvents } from '@sportninja/common/features/event/filterEvents'
import { mergeEventsIntoDayBuckets } from '@sportninja/common/features/event/mergeEventsIntoDayBuckets'
import { useMyScheduleEvents } from '../hooks/useMyScheduleEvents'
import { resolveEventDateWindow } from 'src/context/MyScheduleContext/resolveEventDateWindow'
import ScheduleCardSkeleton from 'src/components/ts-components/ScheduleCardSkeleton'
import EmptyState from 'src/components/ts-components/EmptyState'

const SCROLL_THRESHOLD = 500

/**
 * MyScheduleList component
 * Displays the user's schedule with filters and game list
 */
const MyScheduleList = ({ gameStatusTypes, gameTypes }) => {
  const {
    games,
    loadingGames,
    loadingDates,
    datesLoaded,
    filtersLoaded,
    gamesFetched,
    gamesPagination,
    fetchMoreGames,
    selectedSortOrder,
    selectedDateRange,
    selectedTimePeriod,
    scheduleQuery,
  } = useMyScheduleContext()

  // The SAME query object the games request sends — filters and date window
  // both. Events now come from `/events/membership`, which takes the same
  // params, so passing the shared object is what keeps the two lists bounded
  // identically.
  //
  // No period-change refetch effect any more: the date window is part of this
  // object and therefore part of the query key, so switching Past / Today /
  // Upcoming changes the key and react-query refetches on its own. The old
  // endpoints took no date params, which is why that effect used to be needed.
  const { events } = useMyScheduleEvents({
    query: scheduleQuery,
    enabled: filtersLoaded,
  })

  const [isAppending, setIsAppending] = useState(false)
  const isAppendingRef = useRef(false)

  // Calculate if there are more pages to load
  const hasMore =
    gamesPagination &&
    gamesPagination.current_page < gamesPagination.total_pages

  // Load more games function
  const loadMoreGames = useCallback(async () => {
    if (loadingGames || isAppendingRef.current || !hasMore) {
      return
    }

    setIsAppending(true)
    isAppendingRef.current = true
    try {
      await fetchMoreGames()
    } catch (error) {
      console.error('🚨 Error loading more games:', error)
    } finally {
      setIsAppending(false)
      isAppendingRef.current = false
    }
  }, [loadingGames, hasMore, fetchMoreGames])

  // Handle scroll event
  const handleScroll = useCallback(() => {
    const { scrollTop, scrollHeight, clientHeight } = document.documentElement
    const nearBottom =
      scrollHeight - (scrollTop + clientHeight) < SCROLL_THRESHOLD
    if (nearBottom && hasMore && !loadingGames && !isAppendingRef.current) {
      loadMoreGames()
    }
  }, [hasMore, loadingGames, loadMoreGames])

  // Attach scroll listener
  useEffect(() => {
    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => window.removeEventListener('scroll', handleScroll)
  }, [handleScroll])

  // Show loading while filters are being loaded
  if (!filtersLoaded) {
    return (
      <div
        css={css`
          display: flex;
          align-items: center;
          justify-content: center;
          min-height: 400px;
        `}
      >
        <Spin size='small' />
      </div>
    )
  }

  // Events are keyed in their own timezone by `mergeEventsIntoDayBuckets`, so
  // games must be keyed venue-locally too (not viewer-local dayjs) or an
  // evening event lands a day away from the games it belongs with.
  const eventWindow = resolveEventDateWindow({
    dateRange: selectedDateRange,
    timePeriod: selectedTimePeriod,
  })
  const visibleEvents = clampEventsToLoadedGames({
    events: filterEvents(events, {
      startsAfter: eventWindow.startsAfter,
      startsBefore: eventWindow.startsBefore,
      order: selectedSortOrder,
    }),
    games,
    // Games paginate here while events arrive complete, so hold back events
    // past the last loaded game until the final page is in.
    hasMoreGames: Boolean(hasMore),
    order: selectedSortOrder,
  })

  const dayBuckets = mergeEventsIntoDayBuckets(
    groupGamesByDay(games),
    visibleEvents,
    selectedSortOrder
  )

  const rowCount = dayBuckets.reduce(
    (total, bucket) => total + bucket.items.length,
    0
  )

  return (
    <div
      css={css`
        display: flex;
        flex-direction: column;
        width: 100%;
      `}
    >
      {/* Filter Bar */}
      <FilterBar />

      {/* Separator Line */}
      <div
        css={css`
          height: 1px;
          background-color: ${colors.SECONDARY_200};
          margin-bottom: 16px;
        `}
      />

      {/* Content Area */}
      <div
        css={css`
          display: flex;
          flex-direction: column;
          width: 100%;
          min-width: 0;
          overflow: hidden;
        `}
      >
        {loadingGames || loadingDates || !datesLoaded || !gamesFetched ? (
          // Only the games load gates the skeleton. `eventsLoading` deliberately
          // does not: events go stale in 15s and refetch, and gating on them
          // would replace already-rendered games with a skeleton each time.
          // Events simply appear when they arrive.
          //
          // `!gamesFetched` closes the gap between `datesLoaded` flipping true
          // and the fetch effect actually running. Effects run after paint, so
          // for one frame `loadingGames` is still false and `games` is still
          // its initial `[]` — which reads as "nothing scheduled" and flashes
          // the empty state between two skeletons.
          <ScheduleCardSkeleton />
        ) : rowCount === 0 ? (
          // Empty state
          <div
            css={css`
              display: flex;
              align-items: center;
              justify-content: center;
              min-height: 300px;
              background-color: ${colors.SECONDARY_400};
              border-radius: 8px;
            `}
          >
            <EmptyState
              icon={<CalendarOutlined />}
              title='Nothing scheduled yet'
              subtitle={
                'Nothing is scheduled for this day.\nCheck upcoming or adjust your filters.'
              }
              // The card supplies the height; the default floor would push it
              // taller than the box.
              minHeight='auto'
            />
          </div>
        ) : (
          // Games list
          <>
            <Mobile>
              {(isMobile) => {
                return dayBuckets.map((bucket) => (
                  <DayOfGames
                    key={bucket.dayKey}
                    date={formatCivilDay(bucket.dayKey, 'cccc, LLL d, yyyy')}
                    // Games only — `DayOfGames` knows nothing about events.
                    // Event rows go through its `extraRows` slot instead, so
                    // they sit inside the same collapsible day section.
                    games={bucket.items
                      .filter((row) => row.kind === 'game')
                      .map((row) => row.game)}
                    extraRows={bucket.items
                      .filter((row) => row.kind === 'event')
                      .map((row, idx, rows) => (
                        <EventListRow
                          key={`event-${row.event.id}`}
                          event={row.event}
                          hasNextSibling={idx !== rows.length - 1}
                        />
                      ))}
                    gameStatusTypes={gameStatusTypes}
                    gameTypes={gameTypes}
                    isMobile={isMobile}
                    scheduleId={null}
                    isMySchedule={true}
                  />
                ))
              }}
            </Mobile>
            {/* Infinite scroll footer */}
            <div
              css={css`
                display: flex;
                justify-content: center;
                align-items: center;
                padding: 24px 0;
                min-height: 60px;
              `}
            >
              {isAppending && (
                <div
                  css={css`
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    gap: 8px;
                  `}
                >
                  <Spin size='small' />
                  <Text
                    variant='paragraphSmall'
                    weight='regular'
                    color={colors.NEUTRAL_50}
                    style='font-size: 14px; line-height: 1;'
                  >
                    Loading More...
                  </Text>
                </div>
              )}
              {!hasMore &&
                !loadingGames &&
                games.length > 0 &&
                gamesPagination &&
                gamesPagination.total_pages > 1 && (
                  <Text
                    variant='paragraphSmall'
                    weight='regular'
                    color={colors.NEUTRAL_50}
                    style='font-size: 14px;'
                  >
                    No More Results
                  </Text>
                )}
            </div>
          </>
        )}
      </div>
      <FloatButton.BackTop
        icon={<ArrowUpOutlined />}
        css={css`
          .ant-float-btn-body {
            background-color: ${colors.PRIMARY} !important;
          }
          .ant-float-btn-icon {
            color: ${colors.NEUTRAL_0} !important;
          }
        `}
      />
    </div>
  )
}

const mapStateToProps = (state) => {
  return {
    gameTypes: getAllGameTypes(state),
    gameStatusTypes: getGameStatusTypesBySportId(state, null),
  }
}

export default connect(mapStateToProps)(MyScheduleList)
